import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { useFetchData } from 'services/api/useFetchData';
import { UserType } from '../../views/users/UserTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/button.scss';

// Type of a worker pair as returned by the workerpair endpoints
export type WorkerPairType = {
    id: number;
    foreman: { id: number; full_name: string };
    helper: { id: number; full_name: string };
};

// Type of an assignee option which may be a user or a worker pair
export type AssigneeOptionType = {
    id?: number;
    value?: string;
    full_name: string | null;
    option_group?: string;
    pair?: WorkerPairType;
    [key: string]: any;
};

// Stable params object to prevent refetch loops in the fetch hook
const workerPairFetchParams = { deleted: false };

// Fetches the worker pairs of the environment when the feature is active
export const useWorkerPairs = (): { workerPairsActive: boolean, workerPairs: WorkerPairType[] } => {

    // Check whether the worker pairs feature is active
    const workerPairsActive = useActiveFeature('worker_pairs');

    // Fetch the worker pairs only when the feature is active
    const { response } = useFetchData({
        apiUrl: workerPairsActive ? 'get_workerpair_list' : '',
        params: workerPairFetchParams
    });

    // Memoize the worker pairs to keep a stable reference between renders
    const workerPairs = useMemo(() => (
        workerPairsActive ? (response?.results || []) : []
    ), [workerPairsActive, response]);

    return { workerPairsActive, workerPairs };
};

// Builds grouped assignee options with the worker pairs on top of the users
export const buildAssigneeOptionsWithPairs = (
    users: UserType[],
    workerPairs: WorkerPairType[],
    pairsGroupTitle: string,
    employeesGroupTitle: string
): {
    options: AssigneeOptionType[],
    groupOptions: AssigneeOptionType[] | undefined
} => {

    // Return the plain users without groups when there are no worker pairs
    if (!workerPairs.length) {
        return { options: users as AssigneeOptionType[], groupOptions: undefined };
    }

    // Tag the users with the employees group
    const userOptions: AssigneeOptionType[] = users.map((user) => ({ ...user, option_group: 'pair_group_employees' }));

    // Add the worker pairs as pseudo options within the pairs group
    const pairOptions: AssigneeOptionType[] = workerPairs.map((pair) => ({
        value: `pair-${pair.id}`,
        full_name: `${pair.foreman.full_name} + ${pair.helper.full_name}`,
        option_group: 'pair_group_pairs',
        pair
    }));

    // Configure the group options with the pairs group on top
    const groupOptions: AssigneeOptionType[] = [
        { id: 'pair_group_pairs' as unknown as number, full_name: '', name: pairsGroupTitle },
        { id: 'pair_group_employees' as unknown as number, full_name: '', name: employeesGroupTitle },
    ];

    return { options: [...pairOptions, ...userOptions], groupOptions };
};

// Finds a worker pair by the value of a selected pair pseudo option
export const findWorkerPairByOptionValue = (workerPairs: WorkerPairType[], selectedValue: string): WorkerPairType | undefined => {
    if (!selectedValue.startsWith('pair-')) return undefined;
    return workerPairs.find((pair) => `pair-${pair.id}` === selectedValue);
};

// Dropdown button to mark one of the selected assignees as the foreman
export const ForemanSelect: React.FC<{
    id: string;
    assignees: UserType[];
    foremanId: number | null | undefined;
    onChange: (foremanId: number | null) => void;
}> = ({ id, assignees, foremanId, onChange }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close the dropdown when clicking outside it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Only show the foreman selection when there are two or more assignees
    if (!assignees || assignees.length < 2) return null;

    // Get the currently selected foreman
    const selectedForeman = assignees.find((assignee) => assignee.id === foremanId) || null;

    // Light licenses cannot be the foreman so they are not selectable
    const selectableAssignees = assignees.filter((assignee) => assignee.role_license_type !== 'light_license');

    return (
        <div ref={dropdownRef} className='dropdown-button foreman-select' id={id}>
            <button
                type='button'
                onClick={() => setIsOpen(!isOpen)}
                className={`button button-extra-small button-secondary ${isOpen ? 'button-active' : ''}`}
            >
                <span className='button-text'>
                    {selectedForeman
                        ? `${t('scheduling.worker_pairs.foreman_label')}: ${selectedForeman.full_name}`
                        : t('scheduling.worker_pairs.foreman_label')}
                </span>
                <FontAwesomeIcon
                    icon={!isOpen ? faCaretDown : faCaretUp}
                    style={{ marginLeft: '0.3rem', paddingBottom: '0.025rem' }}
                />
            </button>
            {isOpen && (
                <div className='dropdown-button-menu'>
                    {selectableAssignees.map((assignee) => (
                        <div key={assignee.id}
                             className='dropdown-button-item'
                             onClick={() => {
                                onChange(assignee.id);
                                setIsOpen(false);
                            }}
                        >
                            {assignee.full_name}

                            {assignee.id === foremanId && (
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className='filter-dropdown-icon'
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
